import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

const TYPE_COLORS = {
  reschedule_request: '#D946EF',
  partnership_request: '#0DA7CA',
  pending_appointment: '#F59E0B',
  collaborator_appointment: '#223368',
};

export default function NotificationBell() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], counts: { total: 0 } });
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchNotifs = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const result = await api.getNotifications(currentOrg.id);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  useEffect(() => {
    if (!currentOrg) return;
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [currentOrg, fetchNotifs]);

  const handleClick = (notif) => {
    if (notif.type === 'partnership_request') {
      setActionModal(notif);
      return;
    }
    setOpen(false);
    if (notif.type === 'reschedule_request' || notif.type === 'pending_appointment' || notif.type === 'collaborator_appointment') {
      navigate('/app/scheduling');
    }
  };

  const handlePartnershipAction = async (action) => {
    if (!actionModal || !currentOrg) return;
    setActionLoading(true);
    try {
      if (action === 'accept') {
        await api.acceptPartnership(currentOrg.id, actionModal.partnership_id);
      } else {
        await api.declinePartnership(currentOrg.id, actionModal.partnership_id);
      }
      setActionModal(null);
      fetchNotifs();
    } catch (err) {
      console.error('Failed to handle partnership:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const count = data.counts.total;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-9 h-9 rounded-[10px] border border-theme bg-tertiary text-muted-c text-[15px] flex items-center justify-center hover:bg-hover-surface hover:text-accent-light transition-all relative"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 13.5a2 2 0 0 0 4 0" />
          <path d="M13 6a5 5 0 0 0-10 0c0 2.5-.5 4-1.5 5.5h13C13.5 10 13 8.5 13 6z" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-accent text-white text-[9px] font-bold flex items-center justify-center px-1">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setActionModal(null); }} />
          <div className="absolute right-0 top-full mt-1.5 w-[340px] max-h-[440px] bg-elevated border border-theme rounded-xl shadow-2xl z-50 animate-fade-in overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-theme">
              <span className="text-[12px] font-bold text-primary-c">Notifications</span>
              {count > 0 && (
                <span className="text-[10px] font-semibold text-muted-c">{count}</span>
              )}
            </div>

            <div className="overflow-y-auto max-h-[380px]">
              {loading ? (
                <div className="p-4 space-y-2.5">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-2.5 animate-pulse" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="w-8 h-8 rounded-[8px] bg-tertiary flex-shrink-0" />
                      <div className="flex-1 space-y-1.5 pt-1">
                        <div className="h-3 bg-tertiary rounded w-3/4" />
                        <div className="h-2.5 bg-tertiary rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : data.notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-[12px] text-muted-c">All caught up</p>
                </div>
              ) : (
                data.notifications.map(notif => {
                  const color = TYPE_COLORS[notif.type] || '#6B7280';
                  const isExpanded = actionModal?.id === notif.id;

                  return (
                    <div key={notif.id} className="border-b border-theme last:border-0">
                      <button
                        onClick={() => handleClick(notif)}
                        className="w-full text-left flex gap-2.5 px-4 py-3 hover:bg-hover-surface transition-all"
                      >
                        <div
                          className="w-8 h-8 rounded-[8px] flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
                          style={{ background: color }}
                        >
                          {notif.type === 'reschedule_request' ? 'RS' : notif.type === 'partnership_request' ? 'PR' : notif.type === 'collaborator_appointment' ? 'CO' : 'AP'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-semibold text-primary-c truncate">{notif.title}</div>
                          <div className="text-[10px] text-muted-c mt-0.5">{notif.subtitle}</div>
                          {notif.reason && (
                            <div className="text-[10px] text-secondary-c mt-1 italic truncate">"{notif.reason}"</div>
                          )}
                        </div>
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ background: color }} />
                      </button>

                      {/* Partnership action panel */}
                      {isExpanded && (
                        <div className="px-4 pb-3 animate-fade-in">
                          <div className="bg-tertiary rounded-[10px] p-3.5 border border-theme">
                            <p className="text-[12px] text-secondary-c mb-3">
                              <strong className="text-primary-c">{notif.title.replace(' wants to connect', '')}</strong> wants to partner with your organization. This allows sharing appointment collaborators.
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePartnershipAction('accept')}
                                disabled={actionLoading}
                                className="flex-1 py-[7px] rounded-lg text-[12px] font-semibold text-white gradient-accent shadow-glow transition-all hover:-translate-y-0.5 disabled:opacity-50"
                              >
                                {actionLoading ? '...' : 'Accept'}
                              </button>
                              <button
                                onClick={() => handlePartnershipAction('decline')}
                                disabled={actionLoading}
                                className="flex-1 py-[7px] rounded-lg text-[12px] font-semibold text-muted-c bg-elevated border border-theme hover:bg-hover-surface transition-all disabled:opacity-50"
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
